import { createEvent, restore, sample } from 'effector'
import { fx } from '../../../libs'
import {
  Error,
  Gateway,
  GatewayAccess,
  GatewayCommission,
  GatewayOperation,
  GatewayOperationLimit,
  GatewayWeight,
} from './types'
import { $params } from '../../../features/router'
import { getWithdrawalGroups } from '../../withdrawalGroups/list/model'
import { deleteGateway } from '../list/model'

export const gatewayMounted = createEvent()
export const addGatewayOperations = createEvent<Gateway>()
export const addGatewayOperationLimits = createEvent<Gateway>()
export const setWithdrawalGroupsOption =
  createEvent<{ text: string; value: number }[]>()
export const setGateway = createEvent<Gateway>()
export const setError = createEvent<Error>()
export const submitEditGateway = createEvent<Gateway>()
export const detachExchanger = createEvent<Gateway>()
export const submitSaveAccess = createEvent<GatewayAccess>()
export const submitSaveCommission = createEvent<GatewayCommission>()
export const submitSaveOperation = createEvent<GatewayOperation>()
export const submitSaveOperationLimit = createEvent<GatewayOperationLimit>()
export const submitSaveWeight = createEvent<GatewayWeight>()
export const $gateway = restore(setGateway, {
  gatewayId: 0,
  exchangerId: 0,
  withdrawalGroupId: 0,
  name: '',
  internalName: '',
  praxisGatewayHash: '',
  logo: '',
  file: '',
  psCode: '',
  paymentMethodType: '',
  processingProvider: '',
  depositVerificationRequired: false,
  gatewayOperations: [],
  gatewayCommissions: [],
  gatewayAccess: [],
  gatewayWeight: [],
  withdrawalGroup: {
    id: 0,
    name: '',
  },
  praxisGateway: {
    systemName: '',
    status: '',
    image: '',
    is3D: false,
    isDepositSupported: false,
    isPayoutSupported: false,
    isAuthorizationSupported: false,
    isRefundSupported: false,
  },
  isDisable: false,
  gatewayClientNotification: null,
  technicalCountryCodeA3: null,
  technicalMethodKind: null,
})

export const $error = restore(setError, {
  message: '',
  code: 0,
})
export const $withdrawalGroups = restore(setWithdrawalGroupsOption, [
  {
    text: '',
    value: 1,
  },
])
export const setDeleteAccess = createEvent<GatewayAccess>()
export const setDeleteCommission = createEvent<GatewayCommission>()
export const removeLocalCommission = createEvent<number>()
export const setDeleteOperation = createEvent<GatewayOperation>()
export const setDeleteOperationLimit = createEvent<GatewayOperationLimit>()
export const setDeleteWeight = createEvent<GatewayWeight>()
export const getGateway = fx<void, Gateway>({
  method: 'GET',
  url: '/transaction_request/',
})

export const updateGateway = fx<Gateway, Gateway>({
  method: 'POST',
  url: '/transaction_request/',
})
export const detach = fx<Gateway, Gateway>({
  method: 'POST',
  url: '/gateway/{id}/detach',
})
export const saveAccess = fx<GatewayAccess, void>({
  method: 'POST',
  url: '/gateway/',
})
export const saveCommission = fx<GatewayCommission, void>({
  method: 'POST',
  url: '/gateway/',
})
export const saveOperation = fx<GatewayOperation, void>({
  method: 'POST',
  url: '/gateway/',
})
export const saveOperationLimit = fx<GatewayOperationLimit, void>({
  method: 'POST',
  url: '/gateway/',
})
export const saveWeight = fx<GatewayWeight, void>({
  method: 'POST',
  url: '/gateway/',
})
export const deleteAccess = fx<GatewayAccess, GatewayAccess>({
  method: 'DELETE',
  url: '/gateway/',
})
export const deleteCommission = fx<GatewayCommission, GatewayCommission>({
  method: 'DELETE',
  url: '/gateway/',
})
export const deleteOperation = fx<GatewayOperation, GatewayOperation>({
  method: 'DELETE',
  url: '/gateway/',
})
export const deleteOperationLimit = fx<
  GatewayOperationLimit,
  GatewayOperationLimit
>({
  method: 'DELETE',
  url: '/gateway/',
})
export const deleteWeight = fx<GatewayWeight, GatewayWeight>({
  method: 'DELETE',
  url: '/gateway/',
})
export const addNoticeFX = fx<{ message: string }, any>({
  method: 'POST',
})
export const removeNoticeFx = fx<{ sendEmail: boolean }, any>({
  method: 'PUT',
})

$gateway.on(removeLocalCommission, (state, index) => ({
  ...state,
  gatewayCommissions: state.gatewayCommissions.filter((_, i) => i !== index),
}))

sample({
  clock: gatewayMounted,
  fn: () => {
    let query = {}
    return {
      query,
    }
  },
  target: getWithdrawalGroups,
})

sample({
  clock: getWithdrawalGroups.doneData,
  fn: (doneData) => {
    const wg = doneData.body.items
    return wg.map((item) => ({
      text: item.name,
      value: item.id,
    }))
  },
  target: setWithdrawalGroupsOption,
})

sample({
  clock: gatewayMounted,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.gatewayID
    }
    return {
      url: `/gateway/${id}`,
      query: {
        populate: JSON.stringify(['Gateway.gatewayCommissions']),
      },
    }
  },
  target: getGateway,
})

sample({
  clock: [getGateway.doneData, updateGateway.doneData],
  fn: (doneData) => doneData.body,
  target: [setGateway],
})

sample({
  clock: [
    deleteWeight.doneData,
    deleteOperation.doneData,
    deleteOperationLimit.doneData,
    deleteAccess.doneData,
    saveAccess.doneData,
    deleteCommission.doneData,
    saveCommission.doneData,
    saveOperation.doneData,
    saveOperationLimit.doneData,
    saveWeight.doneData,
    addNoticeFX.doneData,
    removeNoticeFx.doneData,
  ],
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.gatewayID
    }
    return {
      url: `/gateway/${id}`,
      query: {
        populate: JSON.stringify(['Gateway.gatewayCommissions']),
      },
    }
  },
  target: [getGateway],
})

sample({
  clock: submitEditGateway,
  source: $params,
  fn: (params, gateway) => {
    let id
    if (params) {
      id = params.gatewayID
    }
    return {
      url: `/gateway/${id}`,
      formData: { ...gateway },
    }
  },
  target: updateGateway,
})

sample({
  clock: detachExchanger,
  source: $params,
  fn: (params, gateway) => {
    return {
      url: `/gateway/${params?.gatewayID}/detach`,
    }
  },
  target: detach,
})

sample({
  clock: addGatewayOperations,
  fn: (gateway) => {
    let newGO: GatewayOperation = {
      gatewayId: gateway.gatewayId,
      operationType: '',
      processingTime: '',
      limits: '',
      activity: '',
      maintenanceMessage: '',
      maintenanceStart: '',
      maintenanceEnd: '',
      description: '',
      warningMessage: '',
      currencies: '',
      gatewayOperationLimits: [],
    }
    gateway.gatewayOperations.push(newGO)
    return gateway
  },
  target: setGateway,
})

sample({
  clock: setDeleteAccess,
  source: $params,
  fn: (params, ga) => {
    return {
      url: `/gateway/${params?.gatewayID}/access`,
      body: { ...ga },
    }
  },
  target: deleteAccess,
})

sample({
  clock: setDeleteCommission,
  source: $params,
  fn: (params, ga) => {
    return {
      url: `/gateway/${params?.gatewayID}/commission`,
      body: { ...ga },
    }
  },
  target: deleteCommission,
})

sample({
  clock: setDeleteOperation,
  source: $params,
  fn: (params, ga) => {
    return {
      url: `/gateway/${params?.gatewayID}/operation`,
      body: { ...ga },
    }
  },
  target: deleteOperation,
})

sample({
  clock: setDeleteOperationLimit,
  source: $params,
  fn: (params, gol) => {
    return {
      url: `/gateway/${params?.gatewayID}/operation_limit`,
      body: { ...gol },
    }
  },
  target: deleteOperationLimit,
})

sample({
  clock: setDeleteWeight,
  source: $params,
  fn: (params, ga) => {
    return {
      url: `/gateway/${params?.gatewayID}/weight`,
      body: { ...ga },
    }
  },
  target: deleteWeight,
})

sample({
  clock: submitSaveAccess,
  source: $params,
  fn: (params, ga) => {
    return {
      url: `/gateway/${params?.gatewayID}/access`,
      body: { ...ga },
    }
  },
  target: saveAccess,
})

sample({
  clock: submitSaveCommission,
  source: $params,
  fn: (params, ga) => {
    return {
      url: `/gateway/${params?.gatewayID}/commission`,
      body: { ...ga },
    }
  },
  target: saveCommission,
})

sample({
  clock: submitSaveOperation,
  source: $params,
  fn: (params, ga) => {
    return {
      url: `/gateway/${params?.gatewayID}/operation`,
      body: { ...ga },
    }
  },
  target: saveOperation,
})

sample({
  clock: submitSaveOperationLimit,
  source: $params,
  fn: (params, ga) => {
    return {
      url: `/gateway/${params?.gatewayID}/operation_limit`,
      body: { ...ga },
    }
  },
  target: saveOperationLimit,
})

sample({
  clock: submitSaveWeight,
  source: $params,
  fn: (params, gw) => {
    return {
      url: `/gateway/${params?.gatewayID}/weight`,
      body: { ...gw },
    }
  },
  target: saveWeight,
})

sample({
  clock: [
    getGateway.failData,
    saveWeight.failData,
    saveOperation.failData,
    saveAccess.failData,
    saveCommission.failData,
    updateGateway.failData,
    deleteCommission.failData,
    addNoticeFX.failData,
    removeNoticeFx.failData,
  ],
  fn: (failData) => {
    return { message: failData.body.message, code: failData.body.code }
  },
  target: [setError],
})

$error
  .on(gatewayMounted, () => {
    return {
      message: '',
      code: 0,
    }
  })
  .on(submitEditGateway, () => {
    return {
      message: '',
      code: 0,
    }
  })
  .on(submitSaveAccess, () => {
    return {
      message: '',
      code: 0,
    }
  })
  .on(submitSaveCommission, () => {
    return {
      message: '',
      code: 0,
    }
  })
  .on(submitSaveOperation, () => {
    return {
      message: '',
      code: 0,
    }
  })
  .on(submitSaveWeight, () => {
    return {
      message: '',
      code: 0,
    }
  })
